import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * "Probeer onze nieuwste tools uit" — promo card on the Catalog/Landing page.
 * Shown only to non-admin authenticated users. Clicking sends an interest
 * signal to the admin (in-app notification + tracked on the user document so
 * we can throttle to 1 click per 7 days). Already-activated users see an
 * "Open de tool" shortcut instead of the CTA.
 *
 * Renders as an absolutely-positioned card top-right on desktop (overlapping
 * the hero artwork) and as a regular block on mobile so it never overflows.
 */
export default function ToolPromoCard({ user }) {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    // Local "sent" flag — also pre-populated from user.tool_interest if the
    // backend already remembers a recent click (survives reloads).
    const initiallySent = !!(user?.tool_interest?.photo_retouch?.last_requested_at);
    const [sent, setSent] = useState(initiallySent);
    const alreadyActive = !!(user?.tool_access?.photo_retouch);

    const quickIdeas = [
        { key: "declutter", icon: "✨" },
        { key: "trash", icon: "🗑️" },
        { key: "sky", icon: "☀️" },
        { key: "plants", icon: "🪴" },
    ];

    const submit = async () => {
        if (sent || alreadyActive) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/catalog/tool-interest", { tool_key: "photo_retouch" });
            if (data?.already_active) {
                toast.info(t("tool_promo.toast_already_active"));
            } else if (data?.throttled) {
                toast.info(t("tool_promo.toast_throttled"));
            } else {
                toast.success(t("tool_promo.toast_success"));
            }
            setSent(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tool_promo.toast_error"));
        }
        setSubmitting(false);
    };

    return (
        <div
            className="lg:absolute lg:top-28 lg:right-10 lg:w-[360px] mt-10 lg:mt-0 relative z-20"
            data-testid="catalog-tool-promo"
        >
            <div className="relative overflow-hidden bg-gradient-to-br from-[#E5A93D]/[0.08] via-white/[0.03] to-transparent border border-[#E5A93D]/30 hover:border-[#E5A93D]/50 transition-colors rounded-sm p-6 backdrop-blur-sm">
                {/* glow */}
                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#E5A93D]/20 blur-3xl pointer-events-none" />

                <div className="relative">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="h-3.5 w-3.5 text-[#E5A93D]" />
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#E5A93D] font-bold">
                            {t("tool_promo.eyebrow")}
                        </span>
                    </div>

                    <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-3 text-zinc-950">
                        {t("tool_promo.title_1")}
                        <br />
                        <span className="text-[#E5A93D] italic font-medium">{t("tool_promo.title_highlight")}</span>{t("tool_promo.title_2")}
                    </h3>

                    <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                        {t("tool_promo.subtitle")}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mb-6">
                        {quickIdeas.map((idea) => (
                            <span
                                key={idea.key}
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-white border border-zinc-200 rounded-sm text-zinc-600"
                            >
                                <span>{idea.icon}</span>
                                <span>{t(`tool_promo.chips.${idea.key}`)}</span>
                            </span>
                        ))}
                    </div>

                    {alreadyActive ? (
                        <a
                            href="/tools/photo-retouch"
                            data-testid="catalog-tool-promo-open"
                            className="group inline-flex items-center justify-center gap-2 w-full h-10 px-4 bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/25 font-medium rounded-sm transition active:scale-[0.98]"
                        >
                            <Check className="h-3.5 w-3.5" />
                            <span>{t("tool_promo.cta_open")}</span>
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </a>
                    ) : sent ? (
                        <button
                            type="button"
                            disabled
                            data-testid="catalog-tool-promo-sent"
                            className="inline-flex items-center justify-center gap-2 w-full h-10 px-4 bg-zinc-100 border border-zinc-200 text-zinc-500 rounded-sm cursor-not-allowed"
                        >
                            <Check className="h-3.5 w-3.5" />
                            {t("tool_promo.cta_sent")}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="catalog-tool-promo-cta"
                            className="group inline-flex items-center justify-center gap-2 w-full h-11 px-4 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-[0.98] disabled:opacity-60"
                        >
                            <Sparkles className="h-4 w-4" />
                            <span>{submitting ? t("tool_promo.cta_submitting") : t("tool_promo.cta_submit")}</span>
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    )}

                    <p className="mt-3 text-[10px] text-zinc-500 text-center">
                        {t("tool_promo.footer_note")}
                    </p>
                </div>
            </div>
        </div>
    );
}
